import { useState, useEffect } from "react";
import { getConnectionStatus, listChats } from "../services/whatsapp";
import type { WhatsAppSession } from "../types/whatsapp";
import { STATUS_COLORS, STATUS_LABELS, getRolInfo } from "../types/whatsapp";

export default function DashboardPage() {
  const [connectionStatus, setConnectionStatus] = useState<{
    conectado: boolean;
    total_sesiones: number;
    conectadas: number;
    pendientes: number;
    sesiones: WhatsAppSession[];
  } | null>(null);
  const [totalChats, setTotalChats] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [status, chats] = await Promise.all([
          getConnectionStatus().catch(() => null),
          listChats().catch(() => [] as any[]),
        ]);

        if (status) {
          setConnectionStatus({
            conectado: status.conectado,
            total_sesiones: status.total_sesiones,
            conectadas: status.conectadas,
            pendientes: status.pendientes,
            sesiones: status.sesiones || [],
          });
        } else {
          setConnectionStatus({
            conectado: false,
            total_sesiones: 0,
            conectadas: 0,
            pendientes: 0,
            sesiones: [],
          });
        }

        setTotalChats(Array.isArray(chats) ? chats.length : 0);
      } catch {
        // Silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalSessions = connectionStatus?.total_sesiones || 0;
  const connectedSessions = connectionStatus?.conectadas || 0;
  const pendingQR = connectionStatus?.pendientes || 0;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Dashboard</h2>
          <p className="page-subtitle">Resumen de conexiones y actividad</p>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <span>Cargando...</span>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="dashboard-grid">
            <div className="card stat-card">
              <div className="stat-card-header">
                <h3>Estado</h3>
                <span className="stat-icon" style={{ background: "#3b82f620", color: "#3b82f6" }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </span>
              </div>
              <div className="stat-value">{connectionStatus?.conectado ? "Conectado" : "Desconectado"}</div>
              <div className="stat-desc">{connectedSessions} sesión(es) activa(s)</div>
            </div>

            <div className="card stat-card">
              <div className="stat-card-header">
                <h3>Sesiones</h3>
                <span className="stat-icon" style={{ background: "#22c55e20", color: "#22c55e" }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 7V4h16v3" />
                    <path d="M9 20h6" />
                    <path d="M12 4v8" />
                    <path d="M4 11v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6" />
                  </svg>
                </span>
              </div>
              <div className="stat-value">{totalSessions}</div>
              <div className="stat-desc">{connectedSessions} conectadas · {pendingQR} pendientes</div>
            </div>

            <div className="card stat-card">
              <div className="stat-card-header">
                <h3>Chats</h3>
                <span className="stat-icon" style={{ background: "#a855f720", color: "#a855f7" }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </span>
              </div>
              <div className="stat-value">{totalChats}</div>
              <div className="stat-desc">Conversaciones totales</div>
            </div>

            <div className="card stat-card">
              <div className="stat-card-header">
                <h3>QR Pendientes</h3>
                <span className="stat-icon" style={{ background: "#eab30820", color: "#eab308" }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                </span>
              </div>
              <div className="stat-value">{pendingQR}</div>
              <div className="stat-desc">Esperando escaneo</div>
            </div>
          </div>

          {/* Quick info */}
          {!connectionStatus?.conectado && (
            <div className="card">
              <h3>No hay sesión activa</h3>
              <p>Ve a la sección <strong>Sesión WhatsApp</strong> para conectar un número.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}